import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import type { Feature, FeatureCollection, Geometry, MultiPolygon, Polygon } from 'geojson';
import type { OwnMapOwnership, OwnMapTeam } from './ownMapTypes';

/**
 * Census / Natural Earth FIPS renames that still disagree with our DB.
 * Key = geometry id in TopoJSON; value = county_fips in ownership tables.
 */
export const FIPS_ALIASES: Record<string, string> = {
  // Oglala Lakota County, SD (was Shannon County)
  '46102': '46113',
  '46113': '46102',
};

/** Resolve ownership for a geometry FIPS, including known aliases. */
export function ownershipTeamId(
  ownership: Record<string, number>,
  fips: string
): number | undefined {
  const id = String(fips).padStart(5, '0');
  if (ownership[id] != null) return ownership[id];
  const alt = FIPS_ALIASES[id];
  if (alt != null && ownership[alt] != null) return ownership[alt];
  return undefined;
}

export const STATE_ABBR: Record<string, string> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO', '09': 'CT',
  '10': 'DE', '11': 'DC', '12': 'FL', '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL',
  '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME', '24': 'MD',
  '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS', '29': 'MO', '30': 'MT', '31': 'NE',
  '32': 'NV', '33': 'NH', '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC', '46': 'SD',
  '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV',
  '55': 'WI', '56': 'WY', '72': 'PR',
};

/** Natural Earth county topology (TopoJSON). Kept loose for the lab spike. */
export type CountyTopology = {
  type: 'Topology';
  objects: {
    counties: {
      type: 'GeometryCollection';
      geometries: Array<{ type: string; id?: string | number; arcs?: unknown; properties?: { name: string } }>;
    };
  };
  arcs: unknown[];
  transform?: unknown;
  bbox?: number[];
};

export type WorldTopology = {
  type: 'Topology';
  objects: {
    countries: {
      type: 'GeometryCollection';
      geometries: Array<{ type: string; id?: string | number; arcs?: unknown }>;
    };
  };
  arcs: unknown[];
};

export interface PreparedGeo {
  counties: Feature<Polygon | MultiPolygon, { name: string }>[];
  byId: Map<string, Feature<Polygon | MultiPolygon, { name: string }>>;
  nation: Geometry;
  worldLand: Feature[];
  bounds: [[number, number], [number, number]][];
  pt: (id: string) => [number, number];
  topology: CountyTopology;
  geoms: CountyTopology['objects']['counties']['geometries'];
}

// topojson-client typings are stricter than our loose Topology shape.
const tj = topojson as any;

export interface PreparedOwnershipLayers {
  teamIds: string[];
  teamShape: Record<string, Geometry>;
  seamsGeo: Geometry;
  coastGeo: Geometry;
  innerGeo: Geometry;
  /** One marker per contiguous territory blob (`id` unique; `t` = team id). */
  labels: { id: string; t: string; p: [number, number]; a: number; name: string }[];
  amax: number;
}

const EARTH_RADIUS_MI = 3958.7613;

/** Land area of each county geometry, in square miles. Ids are 5-digit FIPS. */
export function countyAreaSqMi(us: CountyTopology): Record<string, number> {
  const fc = tj.feature(us, us.objects.counties) as FeatureCollection<Geometry>;
  const out: Record<string, number> = {};
  for (const feature of fc.features) {
    if (feature.id == null) continue;
    const sqMi = d3.geoArea(feature) * EARTH_RADIUS_MI * EARTH_RADIUS_MI;
    if (!Number.isFinite(sqMi) || sqMi <= 0) continue;
    out[String(feature.id).padStart(5, '0')] = sqMi;
  }
  return out;
}

export async function loadBaseGeo(): Promise<{
  us: CountyTopology;
  world: WorldTopology;
  cents: Record<string, [number, number]>;
}> {
  const [us, world, cents] = await Promise.all([
    fetch('/geo/counties-10m.json').then((r) => r.json()),
    fetch('/geo/countries-110m.json').then((r) => r.json()),
    fetch('/geo/centroids.json').then((r) => r.json()),
  ]);
  return { us, world, cents };
}

export function prepareGeo(
  us: CountyTopology,
  world: WorldTopology,
  cents: Record<string, [number, number]>,
  ownership: OwnMapOwnership
): PreparedGeo {
  const owner = ownership.ownership;
  // Include counties owned under an aliased FIPS (e.g. 46102 ↔ 46113).
  const geoms = us.objects.counties.geometries.filter(
    (g) => ownershipTeamId(owner, String(g.id)) != null
  );
  const filtered: CountyTopology = {
    ...us,
    objects: {
      counties: { ...us.objects.counties, geometries: geoms },
    },
  };
  const counties = (
    tj.feature(filtered, filtered.objects.counties) as FeatureCollection<
      Polygon | MultiPolygon,
      { name: string }
    >
  ).features;
  const byId = new Map(counties.map((f) => [String(f.id), f]));
  const nation = tj.merge(filtered, geoms) as Geometry;
  const bounds = counties.map((f) => d3.geoBounds(f));
  const worldFc = tj.feature(world, world.objects.countries) as FeatureCollection;
  const worldLand = worldFc.features.filter((f) => String(f.id) !== '840');
  const pt = (id: string): [number, number] =>
    cents[id] || (d3.geoCentroid(byId.get(id)!) as [number, number]);

  return {
    counties,
    byId,
    nation,
    worldLand,
    bounds,
    pt,
    topology: filtered,
    geoms,
  };
}

export function prepareOwnershipLayers(
  geo: PreparedGeo,
  ownership: OwnMapOwnership
): PreparedOwnershipLayers {
  const { topology, geoms, counties, pt } = geo;
  const owner = ownership.ownership;
  const teams = ownership.teams;
  const tidOf = (fips: string | number | undefined) =>
    ownershipTeamId(owner, String(fips ?? ''));

  const teamIds = Object.keys(teams).sort(
    (a, b) =>
      geoms.filter((g) => tidOf(g.id) === Number(b)).length -
      geoms.filter((g) => tidOf(g.id) === Number(a)).length
  );

  const teamShape: Record<string, Geometry> = {};
  teamIds.forEach((t) => {
    teamShape[t] = tj.merge(
      topology,
      geoms.filter((g) => tidOf(g.id) === Number(t))
    ) as Geometry;
  });

  const seamsGeo = tj.mesh(
    topology,
    topology.objects.counties,
    (a: { id?: string }, b: { id?: string }) =>
      a !== b && tidOf(a.id) !== tidOf(b.id)
  ) as Geometry;
  const coastGeo = tj.mesh(
    topology,
    topology.objects.counties,
    (a: unknown, b: unknown) => a === b
  ) as Geometry;
  const innerGeo = tj.mesh(
    topology,
    topology.objects.counties,
    (a: { id?: string }, b: { id?: string }) =>
      a !== b && tidOf(a.id) === tidOf(b.id)
  ) as Geometry;

  const nb = tj.neighbors(geoms) as number[][];
  const area = geoms.map((_, i) => d3.geoArea(counties[i]));
  const seen = new Uint8Array(geoms.length);
  /** Every contiguous blob per team (not just the largest). */
  const compsByTeam: Record<string, { a: number; comp: number[] }[]> = {};

  geoms.forEach((g, i) => {
    if (seen[i]) return;
    const t = String(tidOf(g.id));
    if (t === 'undefined' || t === 'null') return;
    const comp: number[] = [];
    const st = [i];
    seen[i] = 1;
    while (st.length) {
      const k = st.pop()!;
      comp.push(k);
      for (const n of nb[k]) {
        if (!seen[n] && String(tidOf(geoms[n].id)) === t) {
          seen[n] = 1;
          st.push(n);
        }
      }
    }
    const a = d3.sum(comp, (k) => area[k]);
    if (!compsByTeam[t]) compsByTeam[t] = [];
    compsByTeam[t].push({ a, comp });
  });

  const labels: PreparedOwnershipLayers['labels'] = [];
  for (const t of teamIds) {
    const blobs = compsByTeam[t];
    if (!blobs?.length) continue;
    blobs.sort((x, y) => y.a - x.a);
    const teamTotal = d3.sum(blobs, (b) => b.a) || 1;
    const team: OwnMapTeam = teams[t];
    const short =
      team.short ||
      team.name.split(' ').slice(0, -1).join(' ') ||
      team.name;

    blobs.forEach((blob, idx) => {
      const { a, comp } = blob;
      // Always keep the largest blob; keep others if they are a real exclave
      // (skip speckles smaller than ~4% of the empire or a single tiny county).
      if (idx > 0 && (a < teamTotal * 0.04 || comp.length < 2)) return;

      let cx = 0;
      let cy = 0;
      let cz = 0;
      for (const ki of comp) {
        const [lo, la] = pt(String(geoms[ki].id)).map((x) => (x * Math.PI) / 180);
        const w = area[ki];
        cx += w * Math.cos(la) * Math.cos(lo);
        cy += w * Math.cos(la) * Math.sin(lo);
        cz += w * Math.sin(la);
      }
      const c: [number, number] = [
        (Math.atan2(cy, cx) * 180) / Math.PI,
        (Math.atan2(cz, Math.hypot(cx, cy)) * 180) / Math.PI,
      ];
      const k = d3.least(comp, (idx2) => d3.geoDistance(pt(String(geoms[idx2].id)), c))!;
      labels.push({
        id: `${t}:${idx}`,
        t,
        p: pt(String(geoms[k].id)),
        a,
        name: short,
      });
    });
  }

  const amax = d3.max(labels, (l) => l.a) || 1;
  return { teamIds, teamShape, seamsGeo, coastGeo, innerGeo, labels, amax };
}

export function shortTeamName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  return parts.slice(0, -1).join(' ');
}
